import { grpc } from '@improbable-eng/grpc-web'
import { NodeHttpTransport } from '@improbable-eng/grpc-web-node-http-transport'
import { GetUserRequest } from '~/stub/apigateway/profile/user_pb'
import { UserService } from '~/stub/apigateway/profile/user_pb_service'
import { GetHasInfoRequest } from '~/stub/apigateway/profile/has_pb'
import { HasService } from '~/stub/apigateway/profile/has_pb_service'
import { FollowRequest } from '~/stub/apigateway/profile/follow_pb'
import { FollowService } from '~/stub/apigateway/profile/follow_pb_service'
import { UserBlockRequest } from '~/stub/apigateway/profile/user_block_pb'
import { UserBlockService } from '~/stub/apigateway/profile/user_block_pb_service'
import { commonMetaData } from '~/store/_helpers/common-helper'
import {
  prefectureTextGetter,
  sexTextGetter,
  corporateFgTextGetter,
  initialUserState,
  initialProviderSummaryOptions
} from '~/store/_helpers/user-helper'
import Promise from 'bluebird'

Promise.config({ cancellation: true })

// state
export const state = () => initialUserState()

// getters
export const getters = {
  prefectureText(state, getters, globalState) {
    return prefectureTextGetter(state, getters, globalState)
  },
  sexText(state, getters, globalState) {
    return sexTextGetter(state, getters, globalState)
  },
  corporateFgText(state, getters, globalState) {
    return corporateFgTextGetter(state, getters, globalState)
  },
  // 出品者属性のユーザーかどうか
  isProviderAttribute(state) {
    return (
      state.hasInfo.hasCareer ||
      state.hasInfo.hasSkill ||
      state.hasInfo.hasPrice ||
      state.hasInfo.hasPortfolio ||
      state.hasInfo.hasService ||
      state.hasInfo.hasBlog ||
      !!state.userJobsList.length
    )
  }
}

// actions
export const actions = {
  async fetchUser(
    { dispatch },
    {
      userId,
      options = initialProviderSummaryOptions(),
      isNotUpdateOption = false,
      userAction = ''
    }
  ) {
    // dispatchの戻り値であるPromise Objectではcancellationが使えないため、このファイルでimportしたBluebirdのnew Promise()でラップする
    const promises = [
      new Promise((resolve, reject) => {
        dispatch('getProviderSummary', { userId, options, isNotUpdateOption, userAction })
          .then(resolve)
          .catch(reject)
      }),
      new Promise((resolve, reject) => {
        dispatch('getUserRelation', userId)
          .then(resolve)
          .catch(reject)
      }),
      new Promise((resolve, reject) => {
        dispatch('getHasInfo', userId)
          .then(resolve)
          .catch(reject)
      })
    ]
    await Promise.all(promises).catch(({ grpcCode }) => {
      promises.forEach(p => p.cancel())
      return Promise.reject({ grpcCode })
    })
  },
  async getProviderSummary(
    { commit, rootState },
    { userId, options, isNotUpdateOption, userAction }
  ) {
    const [{ GetProviderSummaryRequest }, { ProviderSummaryService }] = await Promise.all([
      import('~/stub/apigateway/profile/provider_summary_pb'),
      import('~/stub/apigateway/profile/provider_summary_pb_service')
    ])

    const requestReply = await new Promise((resolve, reject) => {
      const request = new GetProviderSummaryRequest()
      request.setUserId(userId)
      request.setIncludeUserJob(options.userJob)
      request.setIncludeSkill(options.skill)
      request.setIncludeUserSpecialty(options.userSpecialty)
      request.setIncludeLicense(options.license)
      request.setIncludeAward(options.award)
      if (userAction) {
        request.setUserAction(userAction)
      }
      const metadata = commonMetaData(rootState)
      grpc.invoke(ProviderSummaryService.GetProviderSummary, {
        request,
        metadata,
        host: process.env.config.grpcWebUrl,
        transport: NodeHttpTransport(),
        onMessage: message => {
          resolve(message.toObject())
        },
        onEnd: (grpcCode, msg, trailers) => {
          if (grpcCode !== grpc.Code.OK) {
            reject({ grpcCode })
          }
        }
      })
    })
    commit('RECEIVE_USER', requestReply)
    commit('RECEIVE_PROFILE', requestReply)
    commit('RECEIVE_USER_STATUS', requestReply)
    commit('RECEIVE_RATING_SUMMARY', requestReply.ratingSummary)
    if (!isNotUpdateOption) {
      commit('RECEIVE_USER_JOBS', requestReply)
      commit('RECEIVE_SKILLS', requestReply)
      commit('RECEIVE_USER_SPECIALTY', requestReply)
      commit('RECEIVE_LICENSE', requestReply)
      commit('RECEIVE_AWARD', requestReply)
    }
  },
  async getUserRelation({ commit, rootState }, userId) {
    const requestReply = await new Promise((resolve, reject) => {
      const request = new GetUserRequest()
      request.setUserId(userId)
      const metadata = commonMetaData(rootState)
      grpc.invoke(UserService.GetUserRelation, {
        request,
        metadata,
        host: process.env.config.grpcWebUrl,
        transport: NodeHttpTransport(),
        onMessage: message => {
          resolve(message.toObject())
        },
        onEnd: (grpcCode, msg, trailers) => {
          if (grpcCode !== grpc.Code.OK) {
            reject({ grpcCode })
          }
        }
      })
    })
    commit('RECEIVE_USER_RELATION', requestReply)
  },
  async getHasInfo({ commit, rootState }, userId) {
    const requestReply = await new Promise((resolve, reject) => {
      const request = new GetHasInfoRequest()
      request.setUserId(userId)
      const metadata = commonMetaData(rootState)
      grpc.invoke(HasService.GetHasInfo, {
        request,
        metadata,
        host: process.env.config.grpcWebUrl,
        transport: NodeHttpTransport(),
        onMessage: message => {
          resolve(message.toObject())
        },
        onEnd: (grpcCode, msg, trailers) => {
          if (grpcCode !== grpc.Code.OK) {
            reject({ grpcCode })
          }
        }
      })
    })
    commit('RECEIVE_HAS_INFO', requestReply)
  },
  // // NOTE user relation
  async addFollow({ state, dispatch, rootState }) {
    await new Promise((resolve, reject) => {
      const request = new FollowRequest()
      request.setFollowId(state.id)
      const metadata = commonMetaData(rootState)
      grpc.invoke(FollowService.AddFollow, {
        request,
        metadata,
        host: process.env.config.grpcWebUrl,
        transport: NodeHttpTransport(),
        onMessage: message => {
          resolve(message.toObject())
        },
        onEnd: (grpcCode, msg, trailers) => {
          if (grpcCode !== grpc.Code.OK) {
            reject({ grpcCode })
          }
        }
      })
    })
    await dispatch('fetchUser', { userId: state.id, isNotUpdateOption: true })
  },
  async deleteFollow({ state, dispatch, rootState }) {
    await new Promise((resolve, reject) => {
      const request = new FollowRequest()
      request.setFollowId(state.id)
      const metadata = commonMetaData(rootState)
      grpc.invoke(FollowService.DeleteFollow, {
        request,
        metadata,
        host: process.env.config.grpcWebUrl,
        transport: NodeHttpTransport(),
        onMessage: message => {
          resolve(message.toObject())
        },
        onEnd: (grpcCode, msg, trailers) => {
          if (grpcCode !== grpc.Code.OK) {
            reject({ grpcCode })
          }
        }
      })
    })
    await dispatch('fetchUser', { userId: state.id, isNotUpdateOption: true })
  },
  // NOTE: user block
  async addBlock({ state, dispatch, rootState }, { blockeeId }) {
    await new Promise((resolve, reject) => {
      const request = new UserBlockRequest()
      request.setBlockeeId(blockeeId)
      const metadata = commonMetaData(rootState)
      grpc.invoke(UserBlockService.AddUserBlock, {
        request,
        metadata,
        host: process.env.config.grpcWebUrl,
        transport: NodeHttpTransport(),
        onMessage: message => {
          resolve(message.toObject())
        },
        onEnd: (grpcCode, msg, trailers) => {
          if (grpcCode !== grpc.Code.OK) {
            reject({ grpcCode })
          }
        }
      })
    })
    await dispatch('fetchUser', { userId: state.id, isNotUpdateOption: true })
  },
  async deleteBlock({ state, dispatch, rootState }, { blockeeId }) {
    await new Promise((resolve, reject) => {
      const request = new UserBlockRequest()
      request.setBlockeeId(blockeeId)
      const metadata = commonMetaData(rootState)
      grpc.invoke(UserBlockService.DeleteUserBlock, {
        request,
        metadata,
        host: process.env.config.grpcWebUrl,
        transport: NodeHttpTransport(),
        onMessage: message => {
          resolve(message.toObject())
        },
        onEnd: (grpcCode, msg, trailers) => {
          if (grpcCode !== grpc.Code.OK) {
            reject({ grpcCode })
          }
        }
      })
    })
    await dispatch('fetchUser', { userId: state.id, isNotUpdateOption: true })
  }
}

// mutations
export const mutations = {
  RESET(state) {
    Object.assign(state, initialUserState())
  },
  RECEIVE_USER(state, data) {
    Object.assign(state, {
      ...data.user
    })
  },
  RECEIVE_USER_RELATION(state, data) {
    Object.assign(state, { userRelation: data.userRelation })
  },
  RECEIVE_PROFILE(state, data) {
    Object.assign(state, { profile: data.profile })
  },
  RECEIVE_USER_STATUS(state, data) {
    Object.assign(state, { userStatus: data.userStatus })
  },
  RECEIVE_USER_JOBS(state, data) {
    Object.assign(state, { userJobsList: data.userJobsList })
  },
  RECEIVE_SKILLS(state, data) {
    Object.assign(state, { skillsList: data.skillsList })
  },
  RECEIVE_USER_SPECIALTY(state, data) {
    Object.assign(state, { userSpecialtiesList: data.userSpecialtiesList })
  },
  RECEIVE_LICENSE(state, data) {
    Object.assign(state, { licensesList: data.licensesList })
  },
  RECEIVE_AWARD(state, data) {
    Object.assign(state, { awardsList: data.awardsList })
  },
  RECEIVE_RATING_SUMMARY(state, data) {
    Object.assign(state, {
      ratingAvg: data.ratingAvg,
      achievementCount: data.achievementCount,
      followerCount: data.followerCount
    })
  },
  RECEIVE_HAS_INFO(state, data) {
    Object.assign(state, { hasInfo: data.hasInfo })
  }
}
